import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toJS } from 'mobx';
import { cast, getParent, types } from 'mobx-state-tree';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { api } from 'src/api';
import { createAsync } from 'src/utils/asyncModel';
const getInvoicesAsync = createAsync(body => {
  return async function getInvoices() {
    const resp = await api.Payment.getInvoices(body);
    const _getParent = getParent(this),
      invoice = _getParent.invoice;
    invoice.setInvoicesAfterLoad(resp, false);
    return resp;
  };
});
const getInvoicesV2Async = createAsync(body => {
  return async function getInvoices(options) {
    const resp = await api.Payment.getInvoicesV2(body, options);
    const _getParent2 = getParent(this),
      invoice = _getParent2.invoice;
    invoice.setInvoicesAfterLoad(resp, false);
    return resp;
  };
});
const getInvoicesV2LoadMoreAsync = createAsync(body => {
  return async function getInvoices(options) {
    const resp = await api.Payment.getInvoicesV2(body, options);
    const _getParent3 = getParent(this),
      invoice = _getParent3.invoice;
    invoice.setInvoicesAfterLoad(resp, true);
    return resp;
  };
});
const getUpcomingInvoicesAsync = createAsync(() => {
  return async function getUpcomingInvoices(options) {
    const resp = await api.Payment.getUpcomingInvoices(options);
    return resp;
  };
});
const ProductItem = types.model('ProductItem', {
  amountPaid: types.maybeNull(types.number),
  quantity: types.maybeNull(types.number),
  unitPrice: types.maybeNull(types.number)
});
const Invoice = types.model('Invoice', {
  firstNameLine: types.maybeNull(types.string),
  propertyAddress: types.maybeNull(types.string),
  propertyId: types.maybeNull(types.string),
  'Paper Invitation': types.optional(ProductItem, {
    amountPaid: 0,
    quantity: 0,
    unitPrice: 0
  }),
  'Paper Statement': types.optional(ProductItem, {
    amountPaid: 0,
    quantity: 0,
    unitPrice: 0
  }),
  'Unit Subscription Fee': types.optional(ProductItem, {
    amountPaid: 0,
    quantity: 0,
    unitPrice: 0
  }),
  'AppFolio Unit Subscription Fee': types.optional(ProductItem, {
    amountPaid: 0,
    quantity: 0,
    unitPrice: 0
  })
});
const Invoices = types.model('Invoices', {
  amountPaid: types.maybeNull(types.string),
  amountRefunded: types.maybeNull(types.string),
  brand: types.maybeNull(types.string),
  id: types.maybeNull(types.string),
  invoiceItems: types.optional(types.array(Invoice), []),
  invoiceLink: types.maybeNull(types.string),
  invoiceNumber: types.maybeNull(types.string),
  last4: types.maybeNull(types.string),
  paidAt: types.maybeNull(types.string),
  paymentMethodId: types.maybeNull(types.string),
  periodEnd: types.maybeNull(types.string),
  periodName: types.string,
  periodStart: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  stripeInvoiceId: types.maybeNull(types.string)
});
const getFirstNameLine = i => {
  var _i$PaperInvitation, _i$PaperStatement, _i$UnitSubscription, _i$AppFolioUnitSubs;
  if (((_i$PaperInvitation = i['Paper Invitation']) === null || _i$PaperInvitation === void 0 ? void 0 : _i$PaperInvitation.quantity) != null) return 'Paper Invitation';else if (((_i$PaperStatement = i['Paper Statement']) === null || _i$PaperStatement === void 0 ? void 0 : _i$PaperStatement.quantity) != null) return 'Paper Statement';else if (((_i$UnitSubscription = i['Unit Subscription Fee']) === null || _i$UnitSubscription === void 0 ? void 0 : _i$UnitSubscription.quantity) != null) return 'Unit Subscription Fee';else if (((_i$AppFolioUnitSubs = i['AppFolio Unit Subscription Fee']) === null || _i$AppFolioUnitSubs === void 0 ? void 0 : _i$AppFolioUnitSubs.quantity) != null) return 'AppFolio Unit Subscription Fee';else return 'No Products';
};
export const structureInvoiceItems = items => {
  const ids = items.map(i => i.propertyId);
  const uniqueIds = [...new Set(ids)];
  const invoices = uniqueIds.map(id => {
    const invoice = {
      'Paper Invitation': {},
      'Paper Statement': {},
      'Unit Subscription Fee': {},
      'AppFolio Unit Subscription Fee': {},
      firstNameLine: '',
      propertyAddress: '',
      propertyId: ''
    };
    const property = items.filter(i => i.propertyId === id);
    invoice.propertyAddress = property[0].propertyAddress;
    invoice.propertyId = property[0].propertyId;
    property.forEach(i => {
      invoice[i.productName] = {
        amountPaid: i.amountPaid,
        quantity: i.quantity,
        unitPrice: i.unitPrice
      };
    });
    invoice.firstNameLine = getFirstNameLine(invoice);
    return invoice;
  });
  return invoices;
};
const InvoiceStore = types.model('InvoiceStore', {
  filteredInvoicesValue: types.optional(types.array(Invoices), []),
  getInvoicesAsync,
  getInvoicesV2Async,
  getInvoicesV2LoadMoreAsync,
  getUpcomingInvoicesAsync,
  invoicesValue: types.optional(types.array(Invoices), []),
  methodAddedValue: types.optional(types.boolean, false),
  hasMore: types.optional(types.boolean, false),
  page: types.optional(types.number, 0)
}).actions(store => {
  return {
    setFilteredInvoices(data) {
      try {
        store.filteredInvoicesValue = cast(data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setInvoicesAfterLoad(data, isLoadMore) {
      try {
        var _data$response, _data$response2;
        const invoices = data === null || data === void 0 || (_data$response = data.response) === null || _data$response === void 0 || (_data$response = _data$response.invoices) === null || _data$response === void 0 ? void 0 : _data$response.map(i => _objectSpread(_objectSpread({}, i), {}, {
          amountPaid: "$".concat(i.amountPaid),
          amountRefunded: "$".concat(i.amountRefunded),
          id: uuid(),
          invoiceItems: structureInvoiceItems(i.invoiceItems),
          periodEnd: moment(i.periodEnd).format('MM/DD/YYYY'),
          periodName: i.periodEnd ? moment(i.periodEnd).format('MMMM YYYY') : '-',
          periodStart: moment(i.periodStart).format('MM/DD/YYYY')
        }));
        if (isLoadMore) {
          store.filteredInvoicesValue.push(...invoices);
          store.invoicesValue.push(...invoices);
        } else {
          store.filteredInvoicesValue = cast(invoices);
          store.invoicesValue = cast(invoices);
        }
        store.hasMore = data === null || data === void 0 || (_data$response2 = data.response) === null || _data$response2 === void 0 ? void 0 : _data$response2.hasMore;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => ({
  get filteredInvoices() {
    return toJS(store.filteredInvoicesValue);
  },
  get invoices() {
    return toJS(store.invoicesValue);
  }
}));
export const invoice = types.optional(InvoiceStore, {});